/**
 * Liaison DOM du cadrage mobile (plan-cadrage §5 point 3, plan-figures §5.B point 2). Écrit deux longueurs de
 * **contenu**, en px, sur chaque **porteur** (`.pin`, ou chaque `frame.holder`) : `--cad-off` (décalage de la figure
 * dans la boîte collante) et `--cad-h` (hauteur de l'écran, respiration exclue : marges hautes des textes et de la
 * synthèse ; sans textes, celle du bloc du bas). Elles sont héritées par la boîte, les textes, la synthèse et
 * `::after` du porteur (règles K1-K3 et K5 de `styles/mobile.ts`) ; `--cad-off` du cockpit est lue aussi par
 * l'ouverture (fin du fondu du décollage).
 *
 * Le palier est rendu par `sticky` depuis la position de défilement. Le JS ne lit jamais la hauteur
 * d'écran : `svh` est appliqué par la feuille.
 *
 * Garanties : pas d'`innerHeight`/`visualViewport`, pas d'écouteur `scroll`/`resize`, pas de
 * `scrollTo`, pas de minuterie, aucune écriture hors des deux variables, aucun rAF à l'attache
 * (la mesure initiale est synchrone, cas 16 de `landing.test.tsx`).
 *
 * Divergence avec `fitDemo` : le rappel du `ResizeObserver` ne mesure pas, il planifie un seul rAF.
 * La respiration, écrite par la feuille sur les deux marges (textes et synthèse, K3), change la taille de l'élément observé : mesurer
 * dans le rappel provoquerait une notification « ResizeObserver loop » (R-C6).
 */
import { CADRAGE_VARS, cadrageMetrics } from "./cadrage";
import type { CadrageSpec } from "../scene-manifest";
import { offsetWithin } from "./offset";
import type { EngineDeps } from "./scene-engine"; // type seul : aucun cycle à l'exécution

/**
 * Zone morte d'écriture (px). Sans elle, l'arrondi d'`offsetTop` réinjecté par la marge de la synthèse (qui dépend
 * de `--cad-h`) fait osciller la valeur indéfiniment sous WebKit : la boîte collée tremble à l'arrêt.
 */
export const WRITE_DEADBAND_PX = 2;

/**
 * Lie un écran : mesure synchrone, `ResizeObserver` sur la boîte collante, un rAF au plus en vol, écriture
 * entière (px arrondis) avec zone morte de 2 px sur `holder`. `txt === null` (modèle « figure centrée », plan-ecrans point 7) : `textsTop = synthesisTop`, `textsMargin = 0`.
 * @returns détachement idempotent (observateur déconnecté, rAF annulé, variables retirées de `holder`)
 */
function bindScreen(
  holder: HTMLElement,
  box: HTMLElement,
  fig: HTMLElement,
  txt: HTMLElement | null,
  syn: HTMLElement,
  raf: (cb: FrameRequestCallback) => number,
  caf: (h: number) => void,
): () => void {
  const write = (name: string, v: number): void => {
    const r = Math.round(v);
    const cur = holder.style.getPropertyValue(name);
    if (cur !== "" && Math.abs(r - parseFloat(cur)) < WRITE_DEADBAND_PX) return;
    holder.style.setProperty(name, `${r}px`);
  };

  const measure = (): void => {
    const figureTop = offsetWithin(fig, box);
    const synthesisTop = offsetWithin(syn, box);
    const textsTop = txt === null ? synthesisTop : offsetWithin(txt, box);
    if (figureTop === null || textsTop === null || synthesisTop === null) return;
    const m = cadrageMetrics({
      figureTop,
      textsTop,
      textsMargin: txt === null ? 0 : parseFloat(getComputedStyle(txt).marginTop) || 0,
      synthesisTop,
      synthesisMargin: parseFloat(getComputedStyle(syn).marginTop) || 0,
      synthesisHeight: syn.offsetHeight,
    });
    if (m === null) return; // rien n'est écrit ni retiré : les valeurs précédentes restent
    write(CADRAGE_VARS.off, m.off);
    write(CADRAGE_VARS.h, m.h);
  };

  measure();

  let pending: number | null = null;
  let ro: ResizeObserver | null = null;
  if (typeof ResizeObserver !== "undefined") {
    ro = new ResizeObserver(() => {
      if (pending !== null) return;
      pending = raf(() => {
        pending = null;
        measure();
      });
    });
    ro.observe(box);
  }

  return () => {
    ro?.disconnect();
    ro = null;
    if (pending !== null) {
      caf(pending);
      pending = null;
    }
    holder.style.removeProperty(CADRAGE_VARS.off);
    holder.style.removeProperty(CADRAGE_VARS.h);
  };
}

/**
 * Mesure le cadrage de `scope` et tient ses variables à jour, sur un écran ou sur un écran par porteur.
 * - Sans `spec.frame` : porteur `scope > .pin`, boîte `.pin > .in`, nœuds résolus depuis `scope` (passe 6, cockpit).
 * - Avec `spec.frame` : un écran par `scope.querySelectorAll(frame.holder)` ; boîte `:scope > frame.box` ; figure,
 *   textes et synthèse résolus dans le porteur. Un porteur incomplet est ignoré, les autres sont liés.
 * Textes requis seulement si `spec.texts` est déclaré.
 * @param scope section (`.pin-outer#<id>`)
 * @param spec sélecteurs du cadrage
 * @param deps rAF/cAF injectables
 * @returns détachement idempotent de tous les écrans ; no-op si rien n'est lié (scène paresseuse pas encore chargée)
 */
export function bindCadrage(scope: HTMLElement, spec: CadrageSpec, deps?: EngineDeps): () => void {
  // Fermetures paresseuses, partagées par les écrans : aucun appel à l'attache.
  const raf = deps?.raf ?? ((cb: FrameRequestCallback) => requestAnimationFrame(cb));
  const caf = deps?.caf ?? ((h: number) => cancelAnimationFrame(h));
  const { frame, texts } = spec;

  if (!frame) {
    const pin = scope.querySelector<HTMLElement>(":scope > .pin");
    const box = pin?.querySelector<HTMLElement>(":scope > .in") ?? null;
    const fig = scope.querySelector<HTMLElement>(spec.figure);
    const txt = texts === undefined ? null : scope.querySelector<HTMLElement>(texts);
    const syn = scope.querySelector<HTMLElement>(spec.synthesis);
    if (!pin || !box || !fig || !syn || (texts !== undefined && !txt)) return () => {};
    return bindScreen(pin, box, fig, txt, syn, raf, caf);
  }

  const offs: (() => void)[] = [];
  for (const holder of scope.querySelectorAll<HTMLElement>(frame.holder)) {
    const box = holder.querySelector<HTMLElement>(`:scope > ${frame.box}`);
    const fig = holder.querySelector<HTMLElement>(spec.figure);
    const txt = texts === undefined ? null : holder.querySelector<HTMLElement>(texts);
    const syn = holder.querySelector<HTMLElement>(spec.synthesis);
    if (!box || !fig || !syn || (texts !== undefined && !txt)) continue; // porteur incomplet : ignoré
    offs.push(bindScreen(holder, box, fig, txt, syn, raf, caf));
  }
  let detached = false;
  return () => {
    if (detached) return;
    detached = true;
    for (const off of offs) off();
  };
}
